import React, {useContext, useEffect, useRef, useState} from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Image, { IImage } from 'tbk-components/src/components/Image';
import ContentTile, { IContentTileProps } from '../ContentTile';
import AnimatedArcs from '../AnimatedArcs';
import withAppContext from "tbk-components/src/hooks/withAppContext";
import Loading from "tbk-components/src/components/Loading";

/**
 * @useTermData
 * @taxonomy solution
 * @forceLinkedData
 */
export interface ISolutionTax {
	term_id?: number;
	name?: string;
}

/**
 * @useTermData
 * @taxonomy industry
 * @forceLinkedData
 */
export interface IIndustryTax {
	term_id?: number;
	name?: string;
}

/**
 * @useTermData
 * @taxonomy type_classification
 * @forceLinkedData
 */
export interface ITypeClassificationTax {
	term_id?: number;
	name?: string;
}

export interface IContentTileAutomaticSettingsProps {
	/**
	 * Each content tile must have a post type
	 */
	postType?: 'video' | 'white-paper' | 'case-study' | 'news-media' | 'blog' | 'event' | 'webinar' | 'brochure' | 'infographic';
	/**
	 * Leave blank if you want all Solutions
	 */
	solutionTax?: ISolutionTax[];
	/**
	 * Leave blank if you want all Industries
	 */
	industryTax?: IIndustryTax[];
	/**
	 * Leave blank if you want all Type Classifications
	 */
	typeClassificationTax?: ITypeClassificationTax[];
}

export interface IContentTileGroupProps extends IBaseElementProps {
	/**
	 * @noUI
	 */
	context,
	heading?: string;
	/**
	 * @control textarea
	 */
	tagline?: string;
	backgroundImage?: IImage;
	/**
	 * Enable Line Animations - only used when no background image is set
	 */
	enableLineAnimations?: boolean;
	lineAnimationDelay?: number;
	lineAnimationOffset?: number;
	inverted?: boolean;
	/**
	 * @noUI
	 */
	defaultAutoTiles?: IContentTileProps[];
	contentTiles?: IContentTileProps[];
	/**
	 * Enable this option to show the tags on the automatic tiles
	 */
	showAutomaticTileTags?: boolean;
	automaticContentTiles?: IContentTileAutomaticSettingsProps[];
	noSpacing?: boolean;
}

/**
 * Calian Content Tile Group
 * @icon index-card
 * @block
 */
const ContentTileGroup: React.FC<IContentTileGroupProps> = withAppContext( ({
	context,
	id,
	className,
	classNames = [],
	heading,
	tagline,
	backgroundImage,
	enableLineAnimations = true,
	lineAnimationDelay = 250,
	lineAnimationOffset = 150,
	defaultAutoTiles,
	contentTiles,
	automaticContentTiles,
	inverted = false,
	noSpacing = false,
}) => {
	//@ts-ignore
	const {Api} = useContext(context);
	const arcOneColor = 'var(--bs-light)';
	const arcTwoColor = 'var(--bs-light)';
	const [displayTiles, setDisplayTiles] = useState<IContentTileProps[]>(contentTiles || []);
	const [loading, setLoading] = useState<boolean>(false);

	// We'll store the old values in refs to compare
	const prevAutoTilesRef = useRef(automaticContentTiles);
	const prevContentTilesRef = useRef(contentTiles);

	useEffect(() => {
		if ( typeof( automaticContentTiles ) === 'undefined' || automaticContentTiles?.length === 0 ) {
			automaticContentTiles = null;
		}

		if (defaultAutoTiles) {
			if ( contentTiles?.length === 0 ) {
				setDisplayTiles([...defaultAutoTiles]);
			} else {
				setDisplayTiles([...contentTiles, ...defaultAutoTiles]);
			}
		} else {
			const autoTilesChanged =
					JSON.stringify(automaticContentTiles) !==
					JSON.stringify(prevAutoTilesRef.current);
			const contentTilesChanged =
					JSON.stringify(contentTiles) !==
					JSON.stringify(prevContentTilesRef.current);

			// If not changes are to be found then no need to make the API call
			if (!autoTilesChanged && !contentTilesChanged) {
				return;
			}

			prevAutoTilesRef.current = automaticContentTiles;
			prevContentTilesRef.current = contentTiles;
			setLoading(true);
			Api.getAutomaticContentTiles({ 'automaticContentTiles' : automaticContentTiles })
				.then((autoTiles) => {
					if ( autoTiles.length === 0 ) {
						if ( typeof( contentTiles ) === 'undefined' || contentTiles?.length === 0 ) {
							setDisplayTiles( [] );
						} else {
							setDisplayTiles( [ ...contentTiles ] );
						}
					} else {
						if ( typeof( contentTiles ) === 'undefined' || contentTiles?.length === 0 ) {
							setDisplayTiles( [ ...autoTiles ] );
						} else {
							setDisplayTiles( [ ...contentTiles, ...autoTiles ] );
						}
					}
				}).finally(() => {
					setLoading(false);
				});
		}
	}, [ defaultAutoTiles, automaticContentTiles, contentTiles ]);

	return (
		<div
			id={id}
			className={cn(
				...(className
					? [className]
					: [
							'calian-content-tile-group container-fluid position-relative',
							...classNames,
					  ]),
				backgroundImage?.src ? 'has-background' : 'no-background',
			)}
			style={
				backgroundImage
					? {
							background: `url(${backgroundImage.src}), #d9d9d9`,
					  }
					: null
			}
		>
			{loading && (
				<div
					style={{
						position: 'absolute',
						width: '100%',
						height: '100%',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						zIndex: 100,
						background: '#ffffffbd',
					}}
				>
					<Loading />
				</div>
			)}
			<div className="calian-content-tile-group__content mw-1440px mx-auto">
				{backgroundImage && (
					<>
						<div className="calian-content-tile-group__overlay-one"></div>
						<div className="calian-content-tile-group__overlay-two"></div>
					</>
				)}
				<div
					className={cn(
						'calian-content-tile-group__inner container-fluid mw-1240px ps-0 pe-0 position-relative z-1',
						!noSpacing && 'py-5 py-md-12',
					)}
				>
					<div className="row">
						<div className="col-12 text-center">
							{heading && (
								<h2 className="calian-content-tile-group__heading">
									{heading}
								</h2>
							)}
							{tagline && (
								<p className="calian-content-tile-group__tagline subheading mw-627px ms-auto me-auto mb-3 mb-lg-5">
									{tagline}
								</p>
							)}
						</div>
					</div>

					{displayTiles && displayTiles.length > 0 && (
						<div className="row mt-5">
							{displayTiles.map((tile, index) => (
								<div
									className="calian-content-tile-group__column mb-lg-4 col-12 col-lg-4"
									key={index}
								>
									<ContentTile
										inverted={inverted}
										{...tile}
									/>
								</div>
							))}
						</div>
					)}
				</div>
			</div>

			{enableLineAnimations && !backgroundImage && (
				<AnimatedArcs
					arcsStyle={'Component - Two Lines'}
					arcsPosition="Right"
					arcOneColor={arcOneColor}
					arcOneStrokeWidth={4}
					arcTwoColor={arcTwoColor}
					arcTwoStrokeWidth={3}
					animationDelay={lineAnimationDelay}
					animationOffset={lineAnimationOffset}
				/>
			)}
		</div>
	);
});

export default ContentTileGroup;
